import React from "react";
import { Column } from "@/components/Datatable/types";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, Clock } from "lucide-react";
import EventSessionActionMenu from "@/modules/eventRegistration/features/EventSessionManagement/components/EventSessionActionMenu/EventSessionActionMenu";
import { SessionSummary } from "@/modules/eventRegistration/features/EventSessionManagement/api/fetchEventSessions/types";
import { formatSessionDateTime } from "@/modules/eventRegistration/shared/utils/dateFormatting";

interface CreateEventSessionColumnsProps {
  onDeleteSession: (uuid: string) => void;
  onEditSession?: (uuid: string) => void;
  onTogglePublishedSession: (uuid: string, newVal: boolean) => void;
  onWaitlistSession?: (uuid: string) => void;
}

export function createEventSessionColumns({
  onDeleteSession,
  onEditSession,
  onTogglePublishedSession,
  onWaitlistSession,
}: CreateEventSessionColumnsProps): Column<SessionSummary>[] {
  return [
    {
      header: "Start Date",
      accessorKey: "startDate",
      enableSorting: true,
      cell: ({ row }) => {
        const { startDate, timezoneIdentifier, timezoneShortName } =
          row.original;
        return formatSessionDateTime(
          startDate,
          timezoneIdentifier,
          timezoneShortName,
        );
      },
    },
    {
      header: "End Date",
      accessorKey: "endDate",
      enableSorting: true,
      cell: ({ row }) => {
        const { endDate, timezoneIdentifier, timezoneShortName } = row.original;
        return formatSessionDateTime(
          endDate,
          timezoneIdentifier,
          timezoneShortName,
        );
      },
    },
    {
      header: "Max Enrollments",
      accessorKey: "maxEnrollments",
      enableSorting: true,
      cell: ({ row }) => row.original.maxEnrollments ?? "--",
    },
    {
      header: "Published",
      accessorKey: "isPublished",
      enableSorting: true,
      cell: ({ row }) => {
        const { uuid, isPublished } = row.original;

        const stopPropagation = (evt: React.MouseEvent) => {
          evt.stopPropagation();
        };

        const handleChange = (checked: boolean) => {
          onTogglePublishedSession(uuid, checked);
        };

        return (
          <Switch
            checked={Boolean(isPublished)}
            onCheckedChange={handleChange}
            onClick={stopPropagation}
          />
        );
      },
    },
    {
      header: "Finalized",
      accessorKey: "isFinalized",
      enableSorting: true,
      cell: ({ row }) => {
        const { isFinalized } = row.original;

        return (
          <Badge
            className={
              isFinalized
                ? "bg-green-100 text-green-800 border-green-200"
                : "bg-gray-100 text-gray-800 border-gray-200"
            }
            variant={isFinalized ? "default" : "secondary"}
          >
            {isFinalized ? (
              <CheckCircle2 className="h-3 w-3 mr-1" />
            ) : (
              <Clock className="h-3 w-3 mr-1" />
            )}
            {isFinalized ? "Finalized" : "Pending"}
          </Badge>
        );
      },
    },
    {
      header: "Actions",
      id: "actions",
      cell: ({ row }) => (
        <EventSessionActionMenu
          onDeleteSession={onDeleteSession}
          onEditSession={onEditSession}
          onWaitlistSession={onWaitlistSession}
          sessionUuid={row.original.uuid}
        />
      ),
    },
  ];
}
